import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import classes from "./TheatreModeV2.scss";
import classNames from "classnames";
import { getMediaOptions, htmlToText } from "Utils";
import {
  getIconByAttachmentType,
  getAttachmentType,
} from "Utils/GetTheatreModeType";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  MoveArrowsOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "Constants";
import { isPublicPlatformRoute } from "Login/modules/LoginModule";
import {
  isGlobalHeaderOnMultipleScreensEnabled,
  isNewNotesUi,
} from "Post/utils";
import {
  isInKioskMode,
  isMobileOrTablet,
  isSafeExamBrowser,
} from "Utils/DeviceDetectHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import ToddleDocDownloadMenu from "AppComponents/ToddleDoc/components/ToddleDocDownloadMenu";

const googleLinksRegex = /https:\/\/docs.google.com\/(document|spreadsheets|presentation)/;
const googleFormsLinkRegex = /https:\/\/docs\.google\.com\/forms\//;
// Renders media-wise options on right side

const getLabelForOpenInNewTabText = activeAttachment => {
  const type = getAttachmentType(activeAttachment);
  if (_.get(activeAttachment, "metadata.serviceType", "") == "MICROSOFT") {
    return "common:onedrive";
  }
  // Google Forms attachments stay typed as "LINK" (the URL doesn't match the
  // document/spreadsheets/presentation pattern that getAttachmentType rewrites).
  if (
    type === "LINK" &&
    googleFormsLinkRegex.test(_.get(activeAttachment, "url"))
  ) {
    return "common:google_form_label";
  }
  switch (type) {
    case "DOCUMENT":
      return "common:google_docs";
    case "SLIDES":
      return "common:google_slides";
    case "SHEET":
      return "common:google_sheets";
  }
};

const ConfiguredOptions = React.memo(props => {
  const options = getMediaOptions(_.get(props, "configurations", {}));
  const { t } = useTranslation(["common"]);
  const attachmentType = _.get(props, "isAssessmentTool", false)
    ? _.get(props, "isPostRateOnLearningGoals")
      ? "POST_RATE_LEARNING_GOALS"
      : "ASSESSMENT_TOOL"
    : getAttachmentType(_.get(props, "activeAttachment", {}));
  const hideTooltip = isMobileOrTablet();

  const activeAttachment = _.get(props, "activeAttachment", {});
  const isToddleDocAttachment =
    _.get(activeAttachment, "type") === "TODDLE_DOC";
  const onToddleDocDownloadClick = _.get(
    props,
    "configurations.onToddleDocDownloadClick"
  );

  return _.map(_.get(options, attachmentType), (option, index) => {
    if (_.isNil(option) || _.isEmpty(option)) return;
    if (_.has(option, "permissions") && !props.isOptionPermitted(option))
      return;

    const optionContainer = classNames({
      [classes.singleOptionContainer]: true,

      // removig this for now for the ticket JOUR-845 keeping it for future reference
      // [classes.singleOptionWithDivider]:
      //   _.get(option, "hasDivider", false) &&
      //   index < _.size(_.get(options, attachmentType)) - 1,
    });

    const shouldWrapWithToddleDocDownloadMenu =
      option?.key === "common_download" &&
      isToddleDocAttachment &&
      _.isFunction(onToddleDocDownloadClick);

    if (shouldWrapWithToddleDocDownloadMenu) {
      return (
        <div key={option?.key} className={optionContainer}>
          <ToddleDocDownloadMenu
            attachment={activeAttachment}
            onDownload={onToddleDocDownloadClick}
            testIdPrefix="theatremode-toddleDocDownload"
          >
            <ConditionalWrapper
              condition={!hideTooltip}
              type={"wrapperAsElement"}
              wrapper={
                <Tooltip
                  tooltip={t(_.get(option, "label"))}
                  theme="light"
                  showArrow={true}
                />
              }
            >
              <IconButton
                icon={option?.icon}
                size={"small"}
                testId={`theatremode-${option?.key}`}
                type="outlined"
                className="dark"
                _legacy_variant="dark"
                variant="neutral"
              />
            </ConditionalWrapper>
          </ToddleDocDownloadMenu>
        </div>
      );
    }

    return (
      <div key={option?.key} className={optionContainer}>
        {_.isNil(option?.tooltip) ? (
          _.get(option, "buttonType") == "plainButton" ? (
            <ConditionalWrapper
              condition={!hideTooltip}
              type={"wrapperAsElement"}
              wrapper={<Tooltip tooltip={t(_.get(option, "label"))} />}
            >
              <Button
                variant={_.get(option, "variant", "on")}
                onClick={option?.onClick}
                icon={_.get(option, "icon")}
                size={"small"}
                className={classes.editButtonContainer}
                testId={
                  !_.isNil(_.get(option, "key"))
                    ? `theatremode-${_.get(option, "key")}`
                    : undefined
                }
              >
                <div style={{ color: colors.white }}>
                  {props?.t(_.get(option, "label"), {
                    label: props.t(
                      option?.key == "open_link_in_new_tab"
                        ? getLabelForOpenInNewTabText(
                            _.get(props, "activeAttachment")
                          )
                        : _.get(props, "editButtonLabel", "common:attachment")
                    ),
                  })}
                </div>
              </Button>
            </ConditionalWrapper>
          ) : !_.isUndefined(option?.icon) ? (
            <ConditionalWrapper
              condition={!hideTooltip}
              type={"wrapperAsElement"}
              wrapper={
                <Tooltip
                  tooltip={t(_.get(option, "label"))}
                  theme="light"
                  showArrow={true}
                />
              }
            >
              <IconButton
                icon={option?.icon}
                onClick={option?.onClick}
                size={"small"}
                testId={
                  !_.isNil(_.get(option, "key"))
                    ? `theatremode-${_.get(option, "key")}`
                    : undefined
                }
                type="outlined"
                className="dark"
                _legacy_variant="dark"
                variant="neutral"
              />
            </ConditionalWrapper>
          ) : null
        ) : (
          <ConditionalWrapper
            condition={!hideTooltip}
            type={"wrapperAsElement"}
            wrapper={
              <Tooltip
                tooltip={props?.t ? props?.t(option?.tooltip) : ""}
                placement="bottom"
              />
            }
          >
            {!_.isUndefined(option?.icon) ? (
              <IconButton
                icon={option?.icon}
                onClick={option?.onClick}
                size={"small"}
                variant="inline"
                testId={
                  !_.isNil(_.get(option, "key"))
                    ? `theatremode-${_.get(option, "key")}`
                    : undefined
                }
              />
            ) : null}
          </ConditionalWrapper>
        )}
      </div>
    );
  });
});

ConfiguredOptions.displayName = "TheatreModeV2ConfiguredOptions";

// Header - will render close button, file name with icon, current attachment number and media-specific options
const OptionsToolBar = React.memo(props => {
  const {
    toggleTheatreMode,
    testId = "media",
    operationOnCloseClick,
    attachments = [],
    assessmentTools = [],
    activeIndex,
    configurations,
    showEditButton,
    t,
    isRateOnLearningGoalsPresent = false,
    totalItems,
    assessmentToolsEvaluationDetails,
    showReorderAndDeleteAttachmentIcon = false,
    showDeleteAttachmentIcon = false,
    toolbarHeaderStyle,
    showAttachmentCounter,
    allowedPermissions,
    showEditIcon = false,
  } = props;

  const isNewNoteUi = isNewNotesUi();
  const items = [...attachments, ...assessmentTools];
  const activeAttachment = _.get(items, activeIndex, {});
  const modalTitle = !_.isUndefined(
    _.get(activeAttachment, "assessmentToolType")
  )
    ? t("common:evaluation")
    : _.get(activeAttachment, "type") === "TODDLE_DOC"
    ? _.get(activeAttachment, "name", "title") || t("common:untitled")
    : _.get(activeAttachment, "title")
    ? _.get(activeAttachment, "title", "File")
    : !_.isNil(_.get(activeAttachment, "name"))
    ? _.get(activeAttachment, "name", "File")
    : _.get(activeAttachment, "type")
    ? _.capitalize(_.get(activeAttachment, "type", "FILE"))
    : t("common:rate_on_learning_goals");

  const onCloseClick = e => {
    if (e) e?.stopPropagation();
    if (_.isFunction(toggleTheatreMode)) {
      toggleTheatreMode(false);
    }
    if (_.isFunction(operationOnCloseClick)) {
      operationOnCloseClick();
    }
  };

  const isAssessmentToolInQuestion =
    _.get(assessmentToolsEvaluationDetails, "parentType") ==
    "QUESTION_SUBMISSION";

  const isOptionPermitted = option => {
    const permissions = _.get(option, "permissions", []);
    let isOptionPermitted = true;
    _.forEach(permissions, permission => {
      let isPermitted = true;
      switch (permission) {
        case "EDIT_MODE_ONLY": // show only in edit mode
          isPermitted = showEditButton;
          break;
        case "HAS_DOWNLOAD_URL":
          switch (_.get(activeAttachment, "type")) {
            case "WORKBOOK":
              isPermitted = !_.isNil(
                _.get(activeAttachment, "metadata.publicUrl")
              );
              break;
            default:
              isPermitted = true;
              break;
          }
          break;
        case "CAN_OPEN_IN_NEW_TAB":
          if (_.get(activeAttachment, "type") == "LINK") {
            const attachmentUrl = _.get(activeAttachment, "url");
            isPermitted =
              (googleLinksRegex.test(attachmentUrl) ||
                googleFormsLinkRegex.test(attachmentUrl)) &&
              _.get(props, "canOpenGoogleFilesInNewTab", true);
          } else isPermitted = false;
          break;
        case "CAN_DOWNLOAD":
          if (
            isAssessmentToolInQuestion ||
            isSafeExamBrowser() ||
            isInKioskMode()
          ) {
            isPermitted = false;
          } else {
            isPermitted = _.get(props, "canDownloadMedia", true);
          }
          break;
        case "SHOULD_DOWNLOAD_BY_URL":
          // hide download for google doc links
          if (
            _.get(activeAttachment, "type") == "LINK" &&
            googleLinksRegex.test(_.get(activeAttachment, "url"))
          ) {
            isPermitted = false;
          } else {
            isPermitted = true;
          }
          break;
        case "CAN_DOWNLOAD_ASSESSMENT_TOOL":
          if (isAssessmentToolInQuestion) {
            isPermitted = false;
          } else {
            isPermitted = !isPublicPlatformRoute();
          }
          break;
        // this case is used when we have to add the edit button in case of Notes we are passing the allowedPermissions array
        case "NOTES_EDIT_MODE":
          isPermitted = _.includes(allowedPermissions, "NOTES_EDIT_MODE");
          break;
        default:
          isPermitted = true;
          break;
      }
      isOptionPermitted = isOptionPermitted && isPermitted;
    });
    return isOptionPermitted;
  };
  const isGlobalHeaderEnabled = isGlobalHeaderOnMultipleScreensEnabled();
  const isNote = _.get(activeAttachment, "type") === "NOTE";
  const hideTooltip = isMobileOrTablet();
  const closeTooltipProps = {
    label: t("common:close"),
    keys: ["esc"],
    t,
  };
  return (
    <div
      className={classes.toolbarContainer}
      style={{
        ...toolbarHeaderStyle,
        height: isGlobalHeaderEnabled ? "48px" : "56px",
      }}
    >
      <div className={classes.toolbarLeftContainer}>
        {totalItems > 0 ? (
          <div className={classes.fileDetailsContainer}>
            <span className={classes.fileIcon}>
              {getIconByAttachmentType({
                attachment: activeAttachment,
                ...(isNewNoteUi ? isNote && { iconVariant: "warning" } : {}),
              })}
            </span>
            <span
              data-test-id="thearemode-filname"
              className={classes.fileName}
            >
              {isNote
                ? isNewNoteUi
                  ? t("common:note")
                  : htmlToText(modalTitle)
                : htmlToText(modalTitle)}
            </span>
          </div>
        ) : null}
      </div>
      {showAttachmentCounter ? (
        <div
          data-test-id="thearemode-activeattachment"
          className={classes.toolbarCenterContainer}
        >
          {totalItems > 1 ? (
            <div className={classes.countChip}>
              {`${activeIndex + 1} / ${totalItems}`}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className={classes.toolbarRightContainer}>
        {totalItems > 0 ? (
          <>
            <ConfiguredOptions
              testId={testId}
              activeAttachment={activeAttachment}
              configurations={configurations}
              isAssessmentTool={activeIndex >= _.size(attachments)}
              isPostRateOnLearningGoals={
                activeIndex == totalItems - 1 && isRateOnLearningGoalsPresent
              }
              isOptionPermitted={isOptionPermitted}
              editButtonLabel={_.get(props, "editButtonLabel")}
              t={t}
            />
            {showReorderAndDeleteAttachmentIcon ? (
              <>
                {_.size(attachments) > 1 ? (
                  <ConditionalWrapper
                    condition={!hideTooltip}
                    type={"wrapperAsElement"}
                    wrapper={
                      <Tooltip tooltip={t("common:reorder")} theme="light" />
                    }
                  >
                    <IconButton
                      testId={`${testId}-theatremode-reorder`}
                      onClick={props?.onReorderClick}
                      icon={<MoveArrowsOutlined variant="default" />}
                      size={"small"}
                      style={{ marginLeft: "8px" }}
                      type="outlined"
                      className="dark"
                      _legacy_variant="dark"
                      variant="neutral"
                    />
                  </ConditionalWrapper>
                ) : null}
                <ConditionalWrapper
                  condition={!hideTooltip}
                  type={"wrapperAsElement"}
                  wrapper={
                    <Tooltip tooltip={t("common:delete")} theme="light" />
                  }
                >
                  <IconButton
                    testId={`${testId}-theatremode-delete`}
                    onClick={() => {
                      props?.onDeleteMedia && props?.onDeleteMedia(activeIndex);
                      props?.setCurrentAttachmentIndex &&
                        props?.setCurrentAttachmentIndex(
                          activeIndex > 1 ? activeIndex - 1 : 0
                        );
                    }}
                    icon={<DeleteOutlined variant="default" />}
                    size={"small"}
                    style={{ marginLeft: "8px", padding: "8px" }}
                    type="outlined"
                    className="dark"
                    _legacy_variant="dark"
                    variant="neutral"
                  />
                </ConditionalWrapper>
              </>
            ) : null}
            {showEditIcon ? (
              <Button
                variant={"outlined-subtle"}
                onClick={props?.setWorkbookMode}
                size={"small"}
                style={{ marginLeft: "8px" }}
                theme="dark"
                className={classes.reorderMediaButton}
                testId="theatremode-edit"
              >
                {t("common:edit")}
              </Button>
            ) : null}

            {showDeleteAttachmentIcon ? (
              <ConditionalWrapper
                condition={!hideTooltip}
                type={"wrapperAsElement"}
                wrapper={<Tooltip tooltip={t("common:delete")} theme="light" />}
              >
                <IconButton
                  onClick={() => {
                    props?.onDeleteMedia && props?.onDeleteMedia(activeIndex);
                    props?.setCurrentAttachmentIndex &&
                      props?.setCurrentAttachmentIndex(
                        activeIndex > 1 ? activeIndex - 1 : 0
                      );
                  }}
                  icon={<DeleteOutlined variant="on" />}
                  size={"small"}
                  variant={"dark"}
                  style={{ marginLeft: "8px", padding: "8px" }}
                  testId="theatremode-delete"
                />
              </ConditionalWrapper>
            ) : null}
          </>
        ) : null}
        <div style={{ marginLeft: "4px" }}>
          <ConditionalWrapper
            condition={!hideTooltip}
            type={"wrapperAsElement"}
            wrapper={
              <Tooltip
                tooltip={props.getToolTip(closeTooltipProps)}
                placement="bottom"
                theme={"light"}
                showArrow={true}
              />
            }
          >
            <IconButton
              testId={`${testId}-theatremode-close`}
              icon={<CloseOutlined variant={"default"} />}
              _legacy_variant="dark"
              variant="neutral"
              size="small"
              onClick={onCloseClick}
              type="outlined"
              className="dark"
            />
          </ConditionalWrapper>
        </div>
      </div>
    </div>
  );
});

OptionsToolBar.displayName = "TheatreModeToolBar";

export default OptionsToolBar;
